import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  FileUp,
  Loader2,
  MapPin,
  ScanSearch,
  Scissors,
  Upload,
  X,
} from 'lucide-react';
import { useProject } from '../../projects';
import {
  buildingImportSessionApi,
  type BuildingImportSession,
  type CommitResult,
  type FieldMapping,
  type GoogleBuildingsClipResult,
  type ImportSourceType,
  type TransformedPreviewResponse,
} from '../services/building-import-session-api';
import './building-import-wizard.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<ImportSourceType, string> = {
  open_dataset: 'Dataset abierto (ej. Google Open Buildings)',
  official: 'Fuente oficial (catastro, organismo)',
  user_provided: 'Archivo provisto por el usuario',
};

const GOOGLE_DATASET_ID = 'google_open_buildings';

/** Detecta si la configuración apunta a Google Open Buildings. */
function isGoogleOpenBuildings(sourceType: ImportSourceType, sourceDataset: string): boolean {
  return sourceType === 'open_dataset' && sourceDataset.trim() === GOOGLE_DATASET_ID;
}

const MAPPING_FIELDS: Array<{ key: keyof FieldMapping; label: string; required?: boolean }> = [
  { key: 'latitude', label: 'Latitud', required: true },
  { key: 'longitude', label: 'Longitud', required: true },
  { key: 'useType', label: 'Tipo de uso' },
  { key: 'residentialType', label: 'Subtipo residencial' },
  { key: 'floors', label: 'Pisos' },
  { key: 'heightM', label: 'Altura (m)' },
  { key: 'footprintAreaM2', label: 'Superficie footprint (m²)' },
  { key: 'builtAreaM2', label: 'Superficie construida (m²)' },
  { key: 'unitsCount', label: 'Unidades funcionales' },
  { key: 'dwellingsCount', label: 'Viviendas' },
  { key: 'source', label: 'Fuente del dato' },
];

const STEP_LABELS = ['Fuente', 'Archivo', 'Mapping', 'Validar'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getApiErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosErr = error as { response?: { data?: { message?: string } } };
    const msg = axiosErr.response?.data?.message;
    if (msg) return Array.isArray(msg) ? msg.join(', ') : String(msg);
  }
  if (error instanceof Error) return error.message;
  return 'Error inesperado. Verificá los datos e intentá de nuevo.';
}

// ─── Component ────────────────────────────────────────────────────────────────

export function BuildingImportWizardModal(): JSX.Element | null {
  const { projectId } = useProject();

  // Visibility
  const [isOpen, setIsOpen] = useState(false);

  // Wizard state
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 0
  const [sourceType, setSourceType] = useState<ImportSourceType>('user_provided');
  const [sourceDataset, setSourceDataset] = useState('');
  const [sourceName, setSourceName] = useState('');

  // Session
  const [session, setSession] = useState<BuildingImportSession | null>(null);

  // Step 2 mapping
  const [mapping, setMapping] = useState<Partial<FieldMapping>>({});

  // Step 3 preview + commit
  const [preview, setPreview] = useState<TransformedPreviewResponse | null>(null);
  const [commitResult, setCommitResult] = useState<CommitResult | null>(null);
  const [clearPrevious, setClearPrevious] = useState(false);

  // Google clip state
  const [clipResult, setClipResult] = useState<GoogleBuildingsClipResult | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  // Drag over state
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Open/close ──────────────────────────────────────────────────────────

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setStep(0);
    setError(null);
    setSession(null);
    setMapping({});
    setPreview(null);
    setCommitResult(null);
    setClipResult(null);
    setUploadProgress(null);
    setSourceType('user_provided');
    setSourceDataset('');
    setSourceName('');
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // ── Event listener ──────────────────────────────────────────────────────

  useEffect(() => {
    const listener = (): void => { handleOpen(); };
    window.addEventListener('building-open-import-wizard', listener);
    return () => window.removeEventListener('building-open-import-wizard', listener);
  }, [handleOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  // ── Step handlers ────────────────────────────────────────────────────────

  const handleCreateSession = async (): Promise<void> => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const s = await buildingImportSessionApi.createSession(projectId, {
        sourceType,
        sourceDataset: sourceDataset.trim() || undefined,
        sourceName: sourceName.trim() || undefined,
      });
      setSession(s);
      setStep(1);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = useCallback(async (file: File): Promise<void> => {
    if (!projectId || !session) return;
    setLoading(true);
    setError(null);

    if (isGoogleOpenBuildings(sourceType, sourceDataset)) {
      await handleLargeGoogleUpload(projectId, session.id, file);
      setLoading(false);
      return;
    }

    try {
      const s = await buildingImportSessionApi.uploadFile(projectId, session.id, file);
      setSession(s);
      if (s.autoMapping) setMapping(s.autoMapping as Partial<FieldMapping>);
      setStep(2);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, session, sourceType, sourceDataset]);

  const handleLargeGoogleUpload = async (
    pid: string,
    sid: string,
    file: File,
  ): Promise<void> => {
    setUploadProgress(0);
    try {
      const clip = await buildingImportSessionApi.uploadLargeGoogleBuildings(
        pid, sid, file,
        (pct) => setUploadProgress(pct),
      );
      setClipResult(clip);
      setUploadProgress(null);

      // Si no hubo edificios en el AOI, quedarse en step 1 y mostrar diagnóstico.
      if (clip.rowsKept === 0) {
        return;
      }

      // Fetch updated session to get detectedColumns + autoMapping
      const updated = await buildingImportSessionApi.getSession(pid, sid);
      setSession(updated);

      // Pre-fill Google-known mapping
      const gMapping: Partial<FieldMapping> = { latitude: 'latitude', longitude: 'longitude' };
      if (updated.detectedColumns?.includes('area_in_meters')) {
        gMapping.footprintAreaM2 = 'area_in_meters';
      }
      if (updated.autoMapping) {
        setMapping({ ...updated.autoMapping as Partial<FieldMapping>, ...gMapping });
      } else {
        setMapping(gMapping);
      }
      setStep(2);
    } catch (e) {
      setUploadProgress(null);
      setError(getApiErrorMessage(e));
    }
  };

  const handlePutMapping = async (): Promise<void> => {
    if (!projectId || !session) return;
    if (!mapping.latitude || !mapping.longitude) {
      setError('Latitud y longitud son obligatorias.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await buildingImportSessionApi.putMapping(projectId, session.id, mapping as FieldMapping);
      const p = await buildingImportSessionApi.getPreview(projectId, session.id);
      setPreview(p);
      setStep(3);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async (): Promise<void> => {
    if (!projectId || !session) return;
    setLoading(true);
    setError(null);
    try {
      const r = await buildingImportSessionApi.commitSession(projectId, session.id, { clearPrevious });
      setCommitResult(r);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // ── Drag & drop ──────────────────────────────────────────────────────────

  const handleDrop = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) void handleUpload(file);
  }, [handleUpload]);

  // ── Render ───────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  return createPortal(
    <div className="biw-overlay" role="presentation" onClick={handleClose}>
      <div
        className="biw-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Importar edificios"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="biw-header">
          <Building2 size={20} className="biw-header-icon" />
          <div className="biw-header-copy">
            <span className="biw-header-kicker">Edilicio · Importación por sesión</span>
            <span className="biw-header-title">Importar edificios desde CSV</span>
          </div>
          <button type="button" className="biw-close-btn" onClick={handleClose} aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        {/* Steps */}
        <nav className="biw-steps" aria-label="Pasos del wizard">
          {STEP_LABELS.map((label, i) => (
            <span
              key={label}
              className={`biw-step-tab ${i === step ? 'is-active' : ''} ${i < step ? 'is-done' : ''}`}
            >
              {i < step ? <CheckCircle2 size={12} /> : null}
              {label}
            </span>
          ))}
        </nav>

        {/* Body */}
        <div className="biw-body">
          {error && (
            <div className="biw-error" role="alert">
              <AlertCircle size={15} />
              <span>{error}</span>
            </div>
          )}

          {step === 0 && (
            <StepFuente
              sourceType={sourceType}
              onSourceTypeChange={setSourceType}
              sourceDataset={sourceDataset}
              onSourceDatasetChange={setSourceDataset}
              sourceName={sourceName}
              onSourceNameChange={setSourceName}
            />
          )}

          {step === 1 && (
            <StepArchivo
              session={session}
              loading={loading}
              isDragOver={isDragOver}
              fileInputRef={fileInputRef}
              onUpload={handleUpload}
              onDragOver={() => setIsDragOver(true)}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              isGoogleMode={isGoogleOpenBuildings(sourceType, sourceDataset)}
              clipResult={clipResult}
              uploadProgress={uploadProgress}
            />
          )}

          {step === 2 && (
            <StepMapping
              session={session}
              mapping={mapping}
              onMappingChange={setMapping}
            />
          )}

          {step === 3 && (
            <StepValidar
              preview={preview}
              commitResult={commitResult}
              clearPrevious={clearPrevious}
              onClearPreviousChange={setClearPrevious}
            />
          )}
        </div>

        {/* Footer */}
        <footer className="biw-footer">
          <div className="biw-footer-left">
            {step > 0 && !commitResult && (
              <button
                type="button"
                className="biw-btn-secondary"
                onClick={() => { setStep((s) => (s - 1) as 0 | 1 | 2 | 3); setError(null); }}
                disabled={loading}
              >
                <ArrowLeft size={14} />
                Atrás
              </button>
            )}
          </div>

          {step === 0 && (
            <button
              type="button"
              className="biw-btn-primary"
              onClick={() => void handleCreateSession()}
              disabled={loading}
            >
              {loading ? <Loader2 size={14} className="biw-spinner" /> : <ArrowRight size={14} />}
              Crear sesión
            </button>
          )}

          {step === 2 && (
            <button
              type="button"
              className="biw-btn-primary"
              onClick={() => void handlePutMapping()}
              disabled={loading || !mapping.latitude || !mapping.longitude}
            >
              {loading ? <Loader2 size={14} className="biw-spinner" /> : <ScanSearch size={14} />}
              Ver preview
            </button>
          )}

          {step === 3 && !commitResult && (
            <button
              type="button"
              className="biw-btn-success"
              onClick={() => void handleCommit()}
              disabled={loading || (preview?.validationSummary.errorRows ?? 0) === (preview?.validationSummary.totalRows ?? 0)}
            >
              {loading ? <Loader2 size={14} className="biw-spinner" /> : <Upload size={14} />}
              Importar edificios
            </button>
          )}

          {commitResult && (
            <button type="button" className="biw-btn-secondary" onClick={handleClose}>
              Cerrar
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ─── Step sub-components ──────────────────────────────────────────────────────

interface StepFuenteProps {
  sourceType: ImportSourceType;
  onSourceTypeChange: (v: ImportSourceType) => void;
  sourceDataset: string;
  onSourceDatasetChange: (v: string) => void;
  sourceName: string;
  onSourceNameChange: (v: string) => void;
}

function StepFuente({
  sourceType, onSourceTypeChange,
  sourceDataset, onSourceDatasetChange,
  sourceName, onSourceNameChange,
}: StepFuenteProps): JSX.Element {
  const isGoogle = isGoogleOpenBuildings(sourceType, sourceDataset);

  return (
    <div>
      <div className="biw-field-group">
        <span className="biw-label is-required">Tipo de fuente</span>
        <div className="biw-radio-group">
          {(Object.keys(SOURCE_LABELS) as ImportSourceType[]).map((t) => (
            <label key={t} className="biw-radio-label">
              <input type="radio" name="sourceType" value={t} checked={sourceType === t} onChange={() => onSourceTypeChange(t)} />
              {SOURCE_LABELS[t]}
            </label>
          ))}
        </div>
      </div>

      {sourceType === 'open_dataset' && (
        <div className="biw-field-group">
          <span className="biw-label">Dataset</span>
          <div className="biw-radio-group">
            <label className="biw-radio-label">
              <input
                type="radio"
                name="sourceDataset"
                value={GOOGLE_DATASET_ID}
                checked={sourceDataset === GOOGLE_DATASET_ID}
                onChange={() => onSourceDatasetChange(GOOGLE_DATASET_ID)}
              />
              Google Open Buildings
            </label>
            <label className="biw-radio-label">
              <input
                type="radio"
                name="sourceDataset"
                value=""
                checked={sourceDataset !== GOOGLE_DATASET_ID}
                onChange={() => onSourceDatasetChange('')}
              />
              Otro
            </label>
          </div>
        </div>
      )}

      {isGoogle && (
        <div className="biw-google-notice">
          <Scissors size={13} style={{ flexShrink: 0 }} />
          <span>
            Este dataset puede ser muy grande.
            WESTEER recortará el archivo al área del proyecto antes de importarlo.
          </span>
        </div>
      )}

      {sourceType !== 'open_dataset' || sourceDataset !== GOOGLE_DATASET_ID ? (
        <div className="biw-field-group">
          <label className="biw-label">Identificador del dataset</label>
          <input
            className="biw-input"
            value={sourceDataset}
            onChange={(e) => onSourceDatasetChange(e.target.value)}
            placeholder="ej: google_open_buildings_v3"
          />
        </div>
      ) : null}

      <div className="biw-field-group">
        <label className="biw-label">Nombre descriptivo de la fuente</label>
        <input
          className="biw-input"
          value={sourceName}
          onChange={(e) => onSourceNameChange(e.target.value)}
          placeholder="ej: IGN Argentina — Edificaciones 2023"
        />
      </div>
    </div>
  );
}

interface StepArchivoProps {
  session: BuildingImportSession | null;
  loading: boolean;
  isDragOver: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onUpload: (file: File) => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  isGoogleMode: boolean;
  clipResult: GoogleBuildingsClipResult | null;
  uploadProgress: number | null;
}

function StepArchivo({
  session, loading, isDragOver, fileInputRef, onUpload, onDragOver, onDragLeave, onDrop,
  isGoogleMode, clipResult, uploadProgress,
}: StepArchivoProps): JSX.Element {
  const alreadyUploaded = session?.status !== 'draft' && session?.detectedColumns;

  const uploadLabel = isGoogleMode
    ? 'Arrastrá el archivo .csv.gz de Google Open Buildings'
    : 'Arrastrá o hacé clic para seleccionar';

  const uploadHint = isGoogleMode
    ? 'Formato: .csv.gz · Sin límite de tamaño · Se recortará al AOI del proyecto'
    : 'Formatos: .csv · .csv.gz · Máximo 200 MB';

  const loadingLabel = (): string => {
    if (uploadProgress !== null && uploadProgress < 100) return `Subiendo… ${uploadProgress}%`;
    if (uploadProgress === 100 || loading) return 'Recortando al AOI del proyecto…';
    return 'Procesando archivo...';
  };

  return (
    <div>
      {!alreadyUploaded && (
        <div
          className={`biw-upload-area${isDragOver ? ' is-drag-over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); onDragOver(); }}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          {loading ? (
            <>
              <Loader2 size={28} className="biw-spinner" />
              {uploadProgress !== null && uploadProgress < 100 && (
                <div className="biw-upload-progress-bar">
                  <div className="biw-upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </>
          ) : (
            <FileUp size={28} />
          )}
          <p>{loading ? loadingLabel() : uploadLabel}</p>
          <span className="biw-upload-hint">{uploadHint}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.csv.gz,.gz"
            className="biw-upload-area-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); }}
            disabled={loading}
          />
        </div>
      )}

      {clipResult && clipResult.rowsKept === 0 && (
        <div className="biw-clip-result is-error">
          <div className="biw-clip-result-header">
            {clipResult.intersects ? <AlertCircle size={14} /> : <AlertTriangle size={14} />}
            <span>
              {clipResult.intersects
                ? 'El recorte no encontró edificios en el AOI'
                : 'El archivo no corresponde al área del proyecto'}
            </span>
          </div>

          {!clipResult.intersects && (
            <p className="biw-clip-no-intersection-msg">
              El rango espacial del archivo <strong>no intersecta</strong> con los
              límites del proyecto. Verificá que seleccionaste el archivo correcto
              o revisá si el AOI del proyecto está bien definido.
            </p>
          )}

          <div className="biw-clip-bbox-comparison">
            <div className="biw-clip-bbox-panel">
              <span className="biw-clip-bbox-label">Archivo subido</span>
              <code>
                Lat {clipResult.fileBbox.minLat.toFixed(4)} → {clipResult.fileBbox.maxLat.toFixed(4)}<br />
                Lng {clipResult.fileBbox.minLng.toFixed(4)} → {clipResult.fileBbox.maxLng.toFixed(4)}
              </code>
            </div>
            <div className="biw-clip-bbox-panel">
              <span className="biw-clip-bbox-label">AOI del proyecto</span>
              <code>
                Lat {clipResult.projectBbox.minLat.toFixed(4)} → {clipResult.projectBbox.maxLat.toFixed(4)}<br />
                Lng {clipResult.projectBbox.minLng.toFixed(4)} → {clipResult.projectBbox.maxLng.toFixed(4)}
              </code>
            </div>
          </div>

          <div className="biw-clip-stat-row">
            <span>{clipResult.totalRowsScanned.toLocaleString()} filas escaneadas</span>
            <span>0 dentro del AOI</span>
          </div>
        </div>
      )}

      {clipResult && clipResult.rowsKept > 0 && (
        <div className="biw-clip-result">
          <div className="biw-clip-result-header">
            <Scissors size={14} />
            <span>Recorte completado</span>
          </div>
          <div className="biw-clip-stats">
            <div className="biw-clip-stat">
              <strong>{clipResult.totalRowsScanned.toLocaleString()}</strong>
              <span>filas escaneadas</span>
            </div>
            <div className="biw-clip-stat is-ok">
              <strong>{clipResult.rowsKept.toLocaleString()}</strong>
              <span>dentro del AOI</span>
            </div>
            <div className="biw-clip-stat">
              <strong>{clipResult.rowsSkipped.toLocaleString()}</strong>
              <span>descartadas</span>
            </div>
            <div className="biw-clip-stat">
              <strong>{Math.round(clipResult.clippedSizeBytes / 1024).toLocaleString()} KB</strong>
              <span>tamaño final</span>
            </div>
            <div className="biw-clip-stat">
              <strong>{Math.round(clipResult.reductionRatio * 100)}%</strong>
              <span>del dataset</span>
            </div>
          </div>
          {clipResult.rowsKept < 100 && (
            <div className="biw-clip-warn">
              <AlertCircle size={13} />
              Menos de 100 edificios en el AOI. Verificá que el archivo cubra la región correcta.
            </div>
          )}
        </div>
      )}

      {session?.detectedColumns && session.detectedColumns.length > 0 && (
        <>
          <p className="biw-section-title">
            <MapPin size={12} style={{ display: 'inline', marginRight: 4 }} />
            {session.detectedColumns.length} columnas detectadas
          </p>
          <div className="biw-pills">
            {session.detectedColumns.map((col) => (
              <span key={col} className="biw-pill">{col}</span>
            ))}
          </div>

          {session.suggestions && session.suggestions.length > 0 && (
            <>
              <p className="biw-section-title">Sugerencias de mapping automático</p>
              <div className="biw-pills">
                {session.suggestions.map((s) => (
                  <span key={s.internalField} className={`biw-pill is-${s.confidence}`}>
                    {s.internalField} → {s.suggestedColumn}
                  </span>
                ))}
              </div>
            </>
          )}

          {session.previewRows && session.previewRows.length > 0 && (
            <>
              <p className="biw-section-title">Preview (primeras {session.previewRows.length} filas)</p>
              <div className="biw-table-wrapper">
                <table className="biw-table">
                  <thead>
                    <tr>
                      {session.detectedColumns.slice(0, 8).map((col) => (
                        <th key={col}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {session.previewRows.slice(0, 5).map((row, i) => (
                      <tr key={i}>
                        {session.detectedColumns!.slice(0, 8).map((col) => (
                          <td key={col} title={row[col]}>{row[col] ?? '—'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

interface StepMappingProps {
  session: BuildingImportSession | null;
  mapping: Partial<FieldMapping>;
  onMappingChange: (m: Partial<FieldMapping>) => void;
}

function StepMapping({ session, mapping, onMappingChange }: StepMappingProps): JSX.Element {
  const cols = session?.detectedColumns ?? [];

  const setField = (key: keyof FieldMapping, val: string): void => {
    onMappingChange({ ...mapping, [key]: val || undefined });
  };

  return (
    <div>
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 16 }}>
        Asigná cada campo interno a la columna CSV correspondiente. Latitud y longitud son obligatorias.
      </p>
      <div className="biw-mapping-grid">
        {MAPPING_FIELDS.map(({ key, label, required }) => (
          <div key={key} className="biw-field-group">
            <label className={`biw-label${required ? ' is-required' : ''}`}>{label}</label>
            <select
              className="biw-select"
              value={mapping[key] ?? ''}
              onChange={(e) => setField(key, e.target.value)}
            >
              <option value="">— sin asignar —</option>
              {cols.map((col) => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

interface StepValidarProps {
  preview: TransformedPreviewResponse | null;
  commitResult: CommitResult | null;
  clearPrevious: boolean;
  onClearPreviousChange: (v: boolean) => void;
}

function StepValidar({ preview, commitResult, clearPrevious, onClearPreviousChange }: StepValidarProps): JSX.Element {
  if (commitResult) {
    return (
      <div>
        <div className="biw-success-banner">
          <CheckCircle2 size={16} />
          Importación completada correctamente.
        </div>
        <div className="biw-result-grid">
          <div className="biw-result-card">
            <strong>{commitResult.totalRows}</strong>
            <span>filas procesadas</span>
          </div>
          <div className="biw-result-card">
            <strong>{commitResult.importedRows}</strong>
            <span>importadas</span>
          </div>
          <div className="biw-result-card">
            <strong>{commitResult.skippedRows}</strong>
            <span>saltadas</span>
          </div>
        </div>
        {Object.keys(commitResult.issuesByCode).length > 0 && (
          <>
            <p className="biw-section-title">Issues por código</p>
            <div className="biw-pills">
              {Object.entries(commitResult.issuesByCode).map(([code, count]) => (
                <span key={code} className="biw-pill">{code}: {count}</span>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  if (!preview) return <div className="biw-info"><Loader2 size={20} className="biw-spinner" /></div>;

  const { validationSummary: s, validationIssues } = preview;
  const allErrored = s.errorRows > 0 && s.errorRows === s.totalRows;

  return (
    <div>
      <div className="biw-summary-grid">
        <div className="biw-summary-card">
          <strong>{s.totalRows}</strong><span>filas en preview</span>
        </div>
        <div className={`biw-summary-card ${s.errorRows > 0 ? 'is-error' : 'is-ok'}`}>
          <strong>{s.errorRows}</strong><span>con errores</span>
        </div>
        <div className={`biw-summary-card ${s.warningRows > 0 ? 'is-warn' : 'is-ok'}`}>
          <strong>{s.warningRows}</strong><span>con warnings</span>
        </div>
        <div className="biw-summary-card">
          <strong>{s.duplicateCandidates}</strong><span>duplicados</span>
        </div>
        <div className={`biw-summary-card ${s.missingCoordinates > 0 ? 'is-error' : 'is-ok'}`}>
          <strong>{s.missingCoordinates}</strong><span>sin coordenadas</span>
        </div>
        <div className={`biw-summary-card ${s.invalidCoordinates > 0 ? 'is-error' : 'is-ok'}`}>
          <strong>{s.invalidCoordinates}</strong><span>coordenadas inválidas</span>
        </div>
      </div>

      {validationIssues.length > 0 && (
        <>
          <p className="biw-section-title">Issues detectados ({validationIssues.length})</p>
          <div className="biw-issues-list">
            {validationIssues.slice(0, 40).map((issue, i) => (
              <div key={i} className={`biw-issue-row is-${issue.severity}`}>
                <span className="biw-issue-row-idx">fila {issue.rowIndex}</span>
                <span className="biw-issue-row-code">{issue.code}</span>
                <span>{issue.message}</span>
              </div>
            ))}
            {validationIssues.length > 40 && (
              <div className="biw-issue-row" style={{ color: 'rgba(255,255,255,0.35)' }}>
                … y {validationIssues.length - 40} issues más
              </div>
            )}
          </div>
        </>
      )}

      {allErrored ? (
        <div className="biw-error" role="alert">
          <AlertCircle size={15} />
          Todas las filas del preview tienen errores. Revisá el mapping antes de importar.
        </div>
      ) : (
        <label className="biw-checkbox-label" style={{ marginTop: 8 }}>
          <input
            type="checkbox"
            checked={clearPrevious}
            onChange={(e) => onClearPreviousChange(e.target.checked)}
          />
          Eliminar edificios importados previamente por sesión antes de insertar
        </label>
      )}
    </div>
  );
}
